import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../../services/authService";
import "../../styles/AdminReturnDetail.css";

export default function AdminReturnDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [returnRequest, setReturnRequest] = useState(null);
  const [status, setStatus] = useState("");
  const [adminComments, setAdminComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReturn();
  }, [id]);

  const loadReturn = async () => {
    const response = await fetch(`/api/admin/returns/${id}`, {
      credentials: "include",
    });

    if (!response.ok) {
      navigate("/admin/returns");
      return;
    }

    const data = await response.json();

    setReturnRequest(data);
    setStatus(data.status);
    setAdminComments(data.adminComments || "");
  };

  const updateStatus = async () => {
    setSaving(true);

    const csrf = await getCsrfToken();

    const response = await fetch(
      `/api/admin/returns/${id}/status`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify({
          status,
          adminComments,
        }),
      }
    );

    if (response.ok) {
      const updated = await response.json();

      setReturnRequest(updated);
      setStatus(updated.status);
      setAdminComments(updated.adminComments || "");
    }

    setSaving(false);
  };

  const refundReturn = async () => {
    if (
      !window.confirm(
        t("adminReturns.confirmRefund")
      )
    ) {
      return;
    }

    setSaving(true);

    const csrf = await getCsrfToken();

    const response = await fetch(
      `/api/admin/returns/${id}/refund`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          [csrf.headerName]: csrf.token,
        },
      }
    );

    if (response.ok) {
      const updated = await response.json();

      setReturnRequest(updated);
      setStatus(updated.status);
    }

    setSaving(false);
  };

  if (!returnRequest) {
    return null;
  }

  return (
    <main className="admin-return-detail-page">
      <section className="admin-return-detail-container">

        <button
          className="admin-return-detail-back"
          onClick={() =>
            navigate("/admin/returns")
          }
        >
          ← {t("adminReturns.backToReturns")}
        </button>

        <h1>{returnRequest.returnNumber}</h1>

        <section className="admin-return-summary">

          <div>
            <span>{t("adminReturns.order")}</span>
            <strong>
              {returnRequest.orderNumber}
            </strong>
          </div>

          <div>
            <span>{t("adminReturns.requestDate")}</span>
            <strong>
              {new Date(
                returnRequest.requestedAt
              ).toLocaleDateString()}
            </strong>
          </div>

          <div>
            <span>{t("adminReturns.amount")}</span>
            <strong>
              {Number(
                returnRequest.refundAmount
              ).toFixed(2)} €
            </strong>
          </div>

          <div>
            <span>{t("adminReturns.status")}</span>

            <strong
              className={`admin-return-status ${returnRequest.status
                .toLowerCase()
                .replaceAll("_", "-")}`}
            >
              {t(
                `returns.status.${returnRequest.status}`
              )}
            </strong>

          </div>

        </section>

        <section className="admin-return-block">

          <h2>{t("adminReturns.reason")}</h2>

          <p>
            {t(
              `returns.reasons.${returnRequest.reason}`
            )}
          </p>

        </section>

        <section className="admin-return-block">

          <h2>
            {t("adminReturns.customerComments")}
          </h2>

          <p>
            {returnRequest.customerComments ||
              "-"}
          </p>

        </section>

        <section className="admin-return-block">

          <h2>
            {t("adminReturns.adminComments")}
          </h2>

          <textarea
            value={adminComments}
            onChange={(e) =>
              setAdminComments(e.target.value)
            }
          />

        </section>

        <section className="admin-return-block">

          <h2>{t("adminReturns.products")}</h2>

          <div className="admin-return-items">

            {returnRequest.items.map((item) => (

              <article
                key={item.id}
                className="admin-return-item"
              >

                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    alt={item.productName}
                  />
                )}

                <div>

                  <h3>{item.productName}</h3>

                  <p>
                    {t("adminReturns.quantity")}:{" "}
                    {item.quantity}
                  </p>

                </div>

                <strong>
                  {Number(
                    item.refundAmount
                  ).toFixed(2)} €
                </strong>

              </article>

            ))}

          </div>

        </section>

        <section className="admin-return-actions">

          <select
            value={status}
            onChange={(e) =>
              setStatus(e.target.value)
            }
          >

            <option value="REQUESTED">
              {t("returns.status.REQUESTED")}
            </option>

            <option value="APPROVED">
              {t("returns.status.APPROVED")}
            </option>

            <option value="AWAITING_SHIPMENT">
              {t(
                "returns.status.AWAITING_SHIPMENT"
              )}
            </option>

            <option value="RECEIVED">
              {t("returns.status.RECEIVED")}
            </option>

            <option value="REFUND_PENDING">
              {t(
                "returns.status.REFUND_PENDING"
              )}
            </option>

            <option value="REJECTED">
              {t("returns.status.REJECTED")}
            </option>

            <option value="CANCELLED">
              {t("returns.status.CANCELLED")}
            </option>

          </select>

          <button
            onClick={updateStatus}
            disabled={saving}
          >
            {saving
              ? t("adminReturns.saving")
              : t("adminReturns.save")}
          </button>

          {returnRequest.status ===
            "REFUND_PENDING" && (

            <button
              className="admin-return-refund-btn"
              onClick={refundReturn}
              disabled={saving}
            >
              💶{" "}
              {t(
                "adminReturns.refundNow"
              )}
            </button>

          )}

        </section>

      </section>
    </main>
  );
}